/**
 * RequestPaymentEditor
 *
 * Workflow node that sends a payment request to the visitor in the active conversation.
 *
 * How it works:
 *   1. At this point in the workflow, Velaro calls Stripe's Checkout Sessions API to
 *      create a one-time payment link for the specified amount.
 *   2. The payment URL is sent as a message in the conversation (all channels).
 *   3. On Apple Messages for Business, the link renders as a rich interactive card.
 *   4. When the customer completes payment, Stripe fires a webhook to Velaro, which
 *      stores the result in the configured workflow variable.
 *
 * Prerequisites:
 *   - Stripe connected (Integrations → Payments → Stripe)
 *   - Amount and description configured below (fixed or from workflow variable)
 *
 * Channel behavior:
 *   - Web Chat / SMS / WhatsApp: sends the Stripe Checkout URL as a clickable link
 *   - Apple Messages for Business: sends as an interactive rich link card with Apple Pay
 *     as a primary payment method on iPhone (customer never leaves iMessage)
 *   - All other channels: sends the URL as plain text
 *
 * Execution: handled in velaro-messaging WorkflowJobRunner — calls Stripe API directly,
 *   no external proxy. Stripe key is configured per-site in Integrations → Payments.
 */
import * as React from "react";
import { FormGroup } from "../RightAsideHelpers";
import { WorkflowNode } from "../../types";

interface Props {
  node: WorkflowNode;
  onUpdate: (node: Partial<WorkflowNode>) => void;
}

const VAR_HINT = (
  <span className="text-xs text-slate-400 block mt-1">
    Use <code className="bg-slate-100 px-1 rounded">{"<<variableName>>"}</code> to pull from
    a workflow variable set by an earlier <strong>Ask Question</strong> node.
  </span>
);

const CURRENCIES = ["USD", "CAD", "GBP", "EUR", "AUD", "NZD", "SGD", "HKD", "JPY", "MXN"];

export default function RequestPaymentEditor({ node, onUpdate }: Props) {
  const amountMode     = node.paymentAmountMode   ?? "fixed";
  const amountFixed    = node.paymentAmountFixed  ?? "";
  const amountVar      = node.paymentAmountVar    ?? "paymentAmount";
  const currency       = node.paymentCurrency     ?? "USD";
  const description    = node.paymentDescription  ?? "";
  const messageText    = node.paymentMessageText  ?? "Please complete your payment here: <<paymentUrl>>";
  const resultVar      = node.paymentResultVar    ?? "paymentStatus";
  const sessionIdVar   = node.paymentSessionIdVar ?? "paymentSessionId";
  const successUrl     = node.paymentSuccessUrl   ?? "";
  const cancelUrl      = node.paymentCancelUrl    ?? "";

  return (
    <>
      {/* Info banner */}
      <div className="mb-4 rounded-md bg-blue-50 border border-blue-200 px-3 py-2.5 text-xs text-blue-800">
        <p className="font-semibold mb-1">How this node works</p>
        <p className="leading-relaxed">
          Velaro creates a Stripe Checkout session and sends the payment URL to the visitor
          in chat. On <strong>Apple Messages for Business</strong>, this renders as a native
          Apple Pay card — customers pay with Face ID without leaving the conversation.
        </p>
        <p className="mt-1.5">
          <strong>Prerequisite:</strong> Configure your Stripe API key in{" "}
          <a href="/integrations/payments" className="underline font-medium">
            Integrations → Payments
          </a>.
        </p>
      </div>

      {/* Amount */}
      <div className="mb-4 rounded-md bg-slate-50 border border-slate-200 px-3 py-2.5">
        <p className="text-xs font-semibold text-slate-600 mb-2">Payment amount</p>

        <FormGroup label="Amount source">
          <div className="flex gap-4 text-sm mt-1">
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input
                type="radio"
                name="paymentAmountMode"
                value="fixed"
                checked={amountMode === "fixed"}
                onChange={() => onUpdate({ paymentAmountMode: "fixed" })}
              />
              Fixed amount
            </label>
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input
                type="radio"
                name="paymentAmountMode"
                value="variable"
                checked={amountMode === "variable"}
                onChange={() => onUpdate({ paymentAmountMode: "variable" })}
              />
              From workflow variable
            </label>
          </div>
        </FormGroup>

        {amountMode === "fixed" ? (
          <FormGroup label="Amount (e.g. 49.99)">
            <input
              type="number"
              min={0.01}
              step={0.01}
              className="w-40 border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              value={amountFixed}
              onChange={e => onUpdate({ paymentAmountFixed: e.currentTarget.value })}
              placeholder="49.99"
            />
            <span className="text-xs text-slate-400 block mt-1">In the currency selected below. Do not include the currency symbol.</span>
          </FormGroup>
        ) : (
          <FormGroup label="Amount variable">
            <input
              className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              value={amountVar}
              onChange={e => onUpdate({ paymentAmountVar: e.currentTarget.value })}
              placeholder="paymentAmount"
            />
            {VAR_HINT}
          </FormGroup>
        )}

        <FormGroup label="Currency">
          <select
            className="border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            value={currency}
            onChange={e => onUpdate({ paymentCurrency: e.currentTarget.value })}
          >
            {CURRENCIES.map(c => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </FormGroup>
      </div>

      {/* Description */}
      <div className="mb-4 rounded-md bg-slate-50 border border-slate-200 px-3 py-2.5">
        <p className="text-xs font-semibold text-slate-600 mb-2">Payment details</p>

        <FormGroup label="Description (shown on checkout page)">
          <input
            className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            value={description}
            onChange={e => onUpdate({ paymentDescription: e.currentTarget.value })}
            placeholder="e.g. Support fee, Order #1234, Consultation deposit"
          />
          {VAR_HINT}
        </FormGroup>

        <FormGroup label="Message to send in chat">
          <textarea
            rows={2}
            className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            value={messageText}
            onChange={e => onUpdate({ paymentMessageText: e.currentTarget.value })}
          />
          <span className="text-xs text-slate-400 block mt-1">
            Use <code className="bg-slate-100 px-1 rounded">{"<<paymentUrl>>"}</code> where the payment link should appear.
          </span>
        </FormGroup>
      </div>

      {/* Optional redirect URLs */}
      <div className="mb-4 rounded-md bg-slate-50 border border-slate-200 px-3 py-2.5">
        <p className="text-xs font-semibold text-slate-600 mb-1">After-payment redirects (optional)</p>
        <p className="text-xs text-slate-500 mb-3">Leave blank to use your Stripe account defaults.</p>

        <FormGroup label="Success URL">
          <input
            className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            value={successUrl}
            onChange={e => onUpdate({ paymentSuccessUrl: e.currentTarget.value })}
            placeholder="https://yoursite.com/payment-success"
          />
        </FormGroup>

        <FormGroup label="Cancel URL">
          <input
            className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            value={cancelUrl}
            onChange={e => onUpdate({ paymentCancelUrl: e.currentTarget.value })}
            placeholder="https://yoursite.com/payment-cancelled"
          />
        </FormGroup>
      </div>

      {/* Result variables */}
      <div className="mb-4 rounded-md bg-green-50 border border-green-200 px-3 py-2.5">
        <p className="text-xs font-semibold text-green-800 mb-2">Result variables — saved after payment</p>
        <p className="text-xs text-green-700 mb-3 leading-relaxed">
          Use these in subsequent <strong>Condition</strong> or <strong>Send Message</strong> nodes
          to branch on payment success/failure or confirm the transaction.
        </p>

        <FormGroup label="Payment status variable">
          <input
            className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            value={resultVar}
            onChange={e => onUpdate({ paymentResultVar: e.currentTarget.value })}
            placeholder="paymentStatus"
          />
          <span className="text-xs text-slate-400 block mt-1">
            Set to <code className="bg-slate-100 px-1 rounded">paid</code>, <code className="bg-slate-100 px-1 rounded">unpaid</code>, or <code className="bg-slate-100 px-1 rounded">expired</code> by Stripe webhook.
          </span>
        </FormGroup>

        <FormGroup label="Stripe session ID variable">
          <input
            className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            value={sessionIdVar}
            onChange={e => onUpdate({ paymentSessionIdVar: e.currentTarget.value })}
            placeholder="paymentSessionId"
          />
          <span className="text-xs text-slate-400 block mt-1">
            Save the Stripe session ID as a contact property for reconciliation and refunds.
          </span>
        </FormGroup>
      </div>

      {/* After this node hint */}
      <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2.5 text-xs text-amber-800">
        <p className="font-semibold mb-1">Recommended next steps</p>
        <ol className="list-decimal list-inside space-y-1 leading-relaxed">
          <li>
            Add a <strong>Condition</strong> node after this one — check{" "}
            <code className="bg-amber-100 px-1 rounded">{`<<${resultVar}>>`}</code> equals <code className="bg-amber-100 px-1 rounded">paid</code>.
          </li>
          <li>On the success branch, add a <strong>Send Message</strong> confirming the payment.</li>
          <li>On the failure/unpaid branch, offer to retry or transfer to an agent.</li>
          <li>
            Add a <strong>Set Data</strong> node to save{" "}
            <code className="bg-amber-100 px-1 rounded">{`<<${sessionIdVar}>>`}</code> as a contact property for your records.
          </li>
        </ol>
      </div>
    </>
  );
}
